// Flat rates and deductions ("Pauschalen & Abzüge"): the catalog on /flatrates (GET /FlatRate/GetAll) and the
// assignments on process templates ("Standard-Pauschalen/-Abzüge") and event processes. Every catalog entry has
// a kind (FLAT_RATE_KIND): a "Pauschale" pays a recipient — a person (current Orga/Admin, PlayerId) or a role —
// an "Abzug" (e.g. field rent) is paid externally and has no recipient:
// - person row / deduction: due once per (non-cancelled) event of the process, regardless of presence;
// - role row:               due once per present event leader whose role snapshot is that role.
// Amounts: a default for all locations plus optional deviations per location (like textblock variants); null
// means "entfällt". Processes copy the amount of their location at creation (editable per process) and skip
// rows that are "entfällt" there. Limits mirror FlatRateService, flatRateAmountAt mirrors FlatRate.AmountAt.
import { normalizeRole, roleLabel } from './participantGroups';
import { formatEuro } from './payment';

export const FLAT_RATE_NAME_MAX = 100;
export const FLAT_RATE_AMOUNT_MAX = 10000;

export const DELETED_PERSON_LABEL = 'Gelöschte Person';

// Catalog kinds as the API sends them (enum names).
export const FLAT_RATE_KIND = Object.freeze({ flatRate: 'FlatRate', deduction: 'Deduction' });

// Tolerant: 'Deduction' / 1 → 'Deduction', everything else (incl. missing) → 'FlatRate'.
export function normalizeFlatRateKind(kind) {
  return kind === FLAT_RATE_KIND.deduction || kind === 1 || kind === '1'
    ? FLAT_RATE_KIND.deduction
    : FLAT_RATE_KIND.flatRate;
}

// Catalog entry, process row (ProcessFlatRateDto.kind) or editor row (row.kind) of kind "Abzug"?
export const isDeduction = (entryOrRow) => normalizeFlatRateKind(entryOrRow?.kind) === FLAT_RATE_KIND.deduction;

export const flatRateKindLabel = (kind, { plural = false } = {}) =>
  normalizeFlatRateKind(kind) === FLAT_RATE_KIND.deduction
    ? (plural ? 'Abzüge' : 'Abzug')
    : (plural ? 'Pauschalen' : 'Pauschale');

// Material Symbols icon per kind (catalog tiles, row editors, read views).
export const flatRateKindIcon = (kind) =>
  normalizeFlatRateKind(kind) === FLAT_RATE_KIND.deduction ? 'remove_circle' : 'request_quote';

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

// Saved location deviations of a catalog entry, sorted by location name (tolerant: missing list → []).
export function flatRateLocationAmounts(entry) {
  const list = Array.isArray(entry?.locationAmounts) ? entry.locationAmounts : [];
  return [...list].sort((a, b) =>
    (a.locationName ?? '').localeCompare(b.locationName ?? '', 'de', { sensitivity: 'base' }));
}

// Amount of a catalog entry at a location: { amount (number | null = "entfällt"), deviation (dto | null) }.
// The deviation of that location if there is one, else the default — mirrors FlatRate.AmountAt.
export function flatRateAmountAt(entry, locationId) {
  const deviation = locationId == null || !Array.isArray(entry?.locationAmounts)
    ? null
    : entry.locationAmounts.find(a => sameId(a.locationId, locationId)) ?? null;
  const raw = deviation ? deviation.amount : entry?.amount;
  return { amount: raw == null || raw === '' ? null : Number(raw), deviation };
}

// "20,00 €" or "entfällt" (amount null).
export const formatFlatRateAmount = (amount) => (amount == null ? 'entfällt' : formatEuro(amount));

// Locations (from /Location/GetAll) without a deviation in `locationAmounts` yet — choices for "+ Abweichung".
export function locationsWithoutFlatRateAmount(locationAmounts, locations) {
  const taken = new Set((Array.isArray(locationAmounts) ? locationAmounts : []).map(a => String(a.locationId)));
  return (Array.isArray(locations) ? locations : []).filter(l => !taken.has(String(l.id)));
}

export const sortFlatRatesByName = (list) =>
  [...list].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'de', { sensitivity: 'base' }));

// Recipient candidates (GET /FlatRate/RecipientCandidates: current role holders) sorted by name.
export const sortRecipientsByName = (list) =>
  [...list].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'de', { sensitivity: 'base' }));

export const isRoleRecipient = (row) => !!normalizeRole(row?.role);

// Recipient of an assignment row → value of the recipient <select>:
// 'role:Organizer' | 'player:12' | '' (nothing chosen yet, or a person row whose player was deleted).
export function recipientValue(row) {
  const role = normalizeRole(row?.role);
  if (role) return `role:${role}`;
  if (row?.playerId != null) return `player:${row.playerId}`;
  return '';
}

// Inverse of recipientValue → { role, playerId } (exactly one set, or both null).
export function parseRecipientValue(value) {
  if (typeof value === 'string' && value.startsWith('role:')) {
    return { role: normalizeRole(value.slice(5)), playerId: null };
  }
  if (typeof value === 'string' && value.startsWith('player:')) {
    const id = Number(value.slice(7));
    return { role: null, playerId: Number.isInteger(id) && id > 0 ? id : null };
  }
  return { role: null, playerId: null };
}

// Display name of a recipient: '' for a deduction (no recipient), configured role name, else the person name
// (candidate list first, then the name the server sent with the row), else a fallback for deleted / unknown
// persons.
export function recipientLabel(row, candidatesById) {
  if (isDeduction(row)) return '';
  const role = normalizeRole(row?.role);
  if (role) return roleLabel(role);
  if (row?.playerId == null) return DELETED_PERSON_LABEL;
  return candidatesById?.get(Number(row.playerId))?.name
    || row.recipientName
    || row.playerName
    || `Person #${row.playerId}`;
}

// When the amount of a row is due — shown next to amounts ("je Event" / "je anwesendem Orga-Leiter").
export function dueLabel(row) {
  const role = normalizeRole(row?.role);
  return role ? `je anwesendem ${roleLabel(role)}-Leiter` : 'je Event';
}

// Per-event total of process rows (amount as number): fixed part (person rows + deductions) plus per-leader
// sums by role.
export function flatRateSummary(rows) {
  let fixedCents = 0;
  const perLeader = new Map(); // role → cents
  (Array.isArray(rows) ? rows : []).forEach(row => {
    const cents = Math.round(Number(row?.amount ?? 0) * 100);
    if (!Number.isFinite(cents)) return;
    const role = normalizeRole(row?.role);
    if (role) perLeader.set(role, (perLeader.get(role) ?? 0) + cents);
    else fixedCents += cents;
  });
  return {
    fixed: fixedCents / 100,
    perLeader: [...perLeader.entries()].map(([role, cents]) => ({ role, amount: cents / 100 })),
  };
}

// "10,00 € je Event + 12,50 € je anwesendem Orga-Leiter" — '' without rows.
export function formatFlatRateSummary(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return '';
  const { fixed, perLeader } = flatRateSummary(rows);
  const parts = [];
  if (fixed > 0 || perLeader.length === 0) parts.push(`${formatEuro(fixed)} je Event`);
  perLeader.forEach(p => parts.push(`${formatEuro(p.amount)} ${dueLabel({ role: p.role })}`));
  return parts.join(' + ');
}

// API error text for display (plain-text 400/409 bodies); never renders a JSON body.
export function flatRateApiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.title) return data.title;
  if (!err?.response) return 'Keine Verbindung – bitte später erneut versuchen.';
  if (err.response.status === 403) return 'Keine Berechtigung – nur Admins können Pauschalen und Abzüge verwalten.';
  return fallback;
}
